import React from 'react';
import { FormConfig, FormData } from '../types';
import PhotoUpload from './PhotoUpload';

interface FormSectionProps {
  section: FormConfig[0];
  formData: FormData;
  imageErrors: Record<string, string>;
  fieldErrors: Record<string, string>;
  onInputChange: (_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPhotoChange: (_fieldName: string, _files: File[]) => void;
  isCameraMode: boolean;
}

const FormSection: React.FC<FormSectionProps> = ({
  section,
  formData,
  imageErrors,
  fieldErrors,
  onInputChange,
  onPhotoChange,
  isCameraMode
}) => {
  // Accent color mapping for image sections
  const accentClassMap: Record<string, string> = {
    blue: 'border-blue-600',
    green: 'border-green-600',
    primary: 'border-primary',
  };

  return (
    <div className="card">
      <h2 className="text-lg font-medium mb-4">{section.section}</h2>
      {section.fields && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {section.fields.filter(field => 
            // Exclude mileage fields as they're rendered before dashboard image
            field.name !== 'mileage' && 
            field.name !== 'new_mileage' && 
            field.name !== 'return_mileage'
          ).map(field => (
            <div
              className={`form-group${field.fullWidth ? ' sm:col-span-2' : ''}`}
              key={field.name}
              data-field={field.name}
            >
              <label className="form-label" htmlFor={field.name}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  required={field.required}
                  placeholder={field.placeholder}
                  className={`form-input ${fieldErrors[field.name] ? 'border-red-500' : ''}`}
                  value={(formData as any)[field.name] || ''}
                  onChange={onInputChange}
                />
              ) : (
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  required={field.required}
                  placeholder={field.placeholder}
                  min={field.type === 'number' ? '0' : undefined}
                  className={`form-input ${fieldErrors[field.name] ? 'border-red-500' : ''}`}
                  value={(formData as any)[field.name] || ''}
                  onChange={onInputChange}
                />
              )}
              {fieldErrors[field.name] && (
                <div className="text-red-500 text-sm mt-1">{fieldErrors[field.name]}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {section.imageSections && section.imageSections.map(imageSection => {
        // Find mileage field if it exists in this section
        const mileageField = section.fields?.find(field => 
          field.name === 'mileage' || 
          field.name === 'new_mileage' || 
          field.name === 'return_mileage'
        );

        return (
          <div
            key={imageSection.title}
            className={`mb-2 border-l-4 pl-4 rounded-l-md bg-white ${accentClassMap[imageSection.accent] || 'border-primary'}`}
          >
            <div className="font-semibold text-black mb-1">{imageSection.title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {(() => {
                // Calculate which items should be full width
                const dashboardIndex = imageSection.images.findIndex(img => img.name.includes('dashboard'));
                const hasMileageBeforeDashboard = mileageField && dashboardIndex !== -1;
                
                // Count images before dashboard (if mileage field exists)
                const imagesBeforeMileage = hasMileageBeforeDashboard ? dashboardIndex : 0;
                // Count images from dashboard onwards (if mileage field exists)
                const imagesAfterMileage = hasMileageBeforeDashboard 
                  ? imageSection.images.length - dashboardIndex 
                  : imageSection.images.length;
                
                // If we have an odd number of images before the mileage field, make the first one full width
                const shouldFirstBeFullWidth = hasMileageBeforeDashboard && imagesBeforeMileage % 2 === 1;
                // If we have an odd number of images after the mileage field, make the last one full width
                const shouldLastBeFullWidth = imagesAfterMileage % 2 === 1;
                
                return imageSection.images.map((image, idx) => {
                  const isDashboardImage = image.name.includes('dashboard');
                  const isFirstImage = idx === 0;
                  const isLastImage = idx === imageSection.images.length - 1;
                  const shouldBeFullWidth = (isFirstImage && shouldFirstBeFullWidth) || (isLastImage && shouldLastBeFullWidth);
                  
                  return (
                    <React.Fragment key={image.name}>
                      {/* Render mileage field right before dashboard image */}
                      {isDashboardImage && mileageField && (
                        <div className="sm:col-span-2 form-group" data-field={mileageField.name}>
                          <label className="form-label" htmlFor={mileageField.name}>
                            {mileageField.label}
                            {mileageField.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <input
                            id={mileageField.name}
                            name={mileageField.name}
                            type={mileageField.type}
                            required={mileageField.required}
                            placeholder={mileageField.placeholder}
                            min={mileageField.type === 'number' ? '0' : undefined}
                            className={`form-input ${fieldErrors[mileageField.name] ? 'border-red-500' : ''}`}
                            value={(formData as any)[mileageField.name] || ''}
                            onChange={onInputChange}
                          />
                          {fieldErrors[mileageField.name] && (
                            <div className="text-red-500 text-sm mt-1">{fieldErrors[mileageField.name]}</div>
                          )}
                        </div>
                      )}
                      <div className={shouldBeFullWidth ? 'sm:col-span-2' : ''} data-field={image.name}>
                        <PhotoUpload
                          label={image.label}
                          instruction={{ title: image.label, description: image.instructions }}
                          onChange={files => onPhotoChange(image.name, files)}
                          required={image.required}
                          error={imageErrors[image.name]}
                          files={(formData.photos[image.name] || []).filter((file): file is File => file instanceof File)}
                          isCameraMode={isCameraMode}
                        />
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FormSection; 